'use client';

import { useTranslations, useLocale } from 'next-intl';
import { useMemo, useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Typography } from '@/components/ui/typography';
import { getLocalizedPath } from '@/lib/locale-utils';
import * as LucideIcons from 'lucide-react';

type Service = {
  id: string;
  title: string;
  description: string;
  icon?: string | null;
  features?: string[] | null;
};

export function ServicesGrid({ initialServices }: { initialServices?: Service[] } = {}) {
  const t = useTranslations('servicesPage');
  const [services, setServices] = useState<Service[]>(() => initialServices ?? []);
  const [isLoading, setIsLoading] = useState(!initialServices?.length);

  const locale = useLocale();

  useEffect(() => {
    if (initialServices?.length) {
      setServices(initialServices);
      setIsLoading(false);
      return;
    }
    const fetchServices = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`/api/services?locale=${locale}`, {
          cache: 'no-store',
        });
        if (response.ok) {
          const data = await response.json();
          setServices(Array.isArray(data) ? data : []);
        } else {
          setServices([]);
        }
      } catch (error) {
        setServices([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchServices();
  }, [locale, initialServices?.length]);

  const getIconComponent = (iconName?: string | null) => {
    if (!iconName) return null;
    const IconComponent = (LucideIcons as any)[iconName] as React.ComponentType<{ className?: string }> | undefined;
    return IconComponent;
  };

  if (isLoading) {
    return (
      <div className="space-y-8">
        <div className="max-w-3xl mb-4">
          <Typography variant="h2" className="!text-2xl font-bold text-gray-900 dark:text-white mb-2">
            {t('ourServicesTitle')}
          </Typography>
          <Typography variant="p" className="text-base text-gray-600 dark:text-gray-300">
            {t('ourServicesSubtitle')}
          </Typography>
        </div>
        <div className="grid gap-6 md:grid-cols-2">
          {[...Array(6)].map((_, index) => (
            <div
              key={`skeleton-${index}`}
              className="h-64 bg-gray-200 dark:bg-gray-700 rounded-lg animate-pulse"
            />
          ))}
        </div>
      </div>
    );
  }

  if (services.length === 0) {
    return (
      <div className="space-y-8">
        <div className="text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {t('noServicesAvailable') || 'No services available yet.'}
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm mt-2">
            {t('servicesWillAppear') || 'Services will appear here once they are added through the admin panel.'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div className="max-w-3xl mb-4">
        <Typography variant="h2" className="mb-4">
          {t('ourServicesTitle')}
        </Typography>
        <Typography variant="p" className="text-base text-gray-600 dark:text-gray-300">
          {t('ourServicesSubtitle')}
        </Typography>
      </div>
      <div className="grid gap-6 md:grid-cols-2">
        {services.map((service, index) => {
          const Icon = getIconComponent(service.icon) || undefined;

          return (
            <Card
              key={service.id}
              title={service.title}
              description=""
              icon={Icon}
              index={index}
              showIndex={false}
              showFooterLink={false}
              animationDelay={index * 80}
              titleVariant="h2"
              renderContent={
                <div className="relative z-10 h-full flex flex-col">
                  {/* Icon */}
                  {Icon && (
                    <div className="mb-5">
                      <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 group-hover:bg-primary-100 dark:group-hover:bg-primary-900/30 transition-colors duration-300">
                        <Icon className="w-8 h-8 text-primary-600 dark:text-primary-400" />
                      </div>
                    </div>
                  )}

                  {/* Title */}
                  <Typography variant="h4" className="mb-3">
                    {service.title}
                  </Typography>

                  {/* Description/Content */}
                  {service.description && (
                    <Typography variant="p" className="text-md text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                      {service.description}
                    </Typography>
                  )}

                  {/* Features List */}
                  {service.features && service.features.length > 0 && (
                    <ul className="space-y-2 flex-1">
                      {service.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-start text-md text-gray-700 dark:text-gray-300"
                        >
                          <span className="text-primary-600 dark:text-primary-400 mr-2 mt-0.5 flex-shrink-0">
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M5 13l4 4L19 7"
                              />
                            </svg>
                          </span>
                          <span>{feature}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              }
            />
          );
        })}
      </div>
    </div>
  );
}

